// src/widgets/kpi-card.tsx
import { cn } from "@modootoday/ui-primitives/core";
import { jsx, jsxs } from "react/jsx-runtime";
var TONE_CLASS = {
  neutral: "",
  positive: "text-signal-success",
  negative: "text-signal-danger",
  brand: "text-text-brand"
};
function formatValue(value, format) {
  if (typeof value === "string") return value;
  if (format === "percent") return `${value.toFixed(1)}%`;
  if (format === "currency") return value.toLocaleString("ko-KR");
  if (format === "compact") {
    if (Math.abs(value) >= 1e8) return `${(value / 1e8).toFixed(1)}\uC5B5`;
    if (Math.abs(value) >= 1e4) return `${(value / 1e4).toFixed(1)}\uB9CC`;
    return value.toLocaleString("ko-KR");
  }
  return value.toLocaleString("ko-KR");
}
function formatDelta(delta) {
  const sign = delta.value > 0 ? "+" : "";
  if (delta.format === "absolute")
    return `${sign}${delta.value.toLocaleString("ko-KR")}`;
  return `${sign}${(delta.value * 100).toFixed(1)}%`;
}
function KpiCard({
  label,
  value,
  format,
  unit,
  delta,
  tone = "neutral",
  sparkline,
  loading,
  error,
  className,
  onClick
}) {
  if (loading) {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "rounded-sm border border-stroke-subtle bg-surface-raised p-4",
          className
        ),
        children: [
          /* @__PURE__ */ jsx("div", { className: "skeleton-shimmer h-3 w-16 rounded-xs" }),
          /* @__PURE__ */ jsx("div", { className: "skeleton-shimmer mt-3 h-7 w-28 rounded-xs" }),
          /* @__PURE__ */ jsx("div", { className: "skeleton-shimmer mt-2 h-3 w-12 rounded-xs" })
        ]
      }
    );
  }
  if (error) {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "rounded-sm border border-stroke-subtle bg-surface-raised p-4",
          className
        ),
        children: [
          /* @__PURE__ */ jsx("p", { className: "text-xs text-text-tertiary", children: label }),
          /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-signal-danger", children: "\uBD88\uB7EC\uC624\uC9C0 \uBABB\uD588\uC5B4\uC694" })
        ]
      }
    );
  }
  const Wrapper = onClick ? "button" : "div";
  const deltaTone = delta && delta.value > 0 ? "positive" : delta && delta.value < 0 ? "negative" : "neutral";
  return /* @__PURE__ */ jsxs(
    Wrapper,
    {
      type: onClick ? "button" : void 0,
      onClick,
      className: cn(
        "text-left rounded-sm border border-stroke-subtle bg-surface-raised p-4 transition-colors",
        onClick && "hover:bg-surface-sunken cursor-pointer",
        className
      ),
      children: [
        /* @__PURE__ */ jsx("p", { className: "text-xs text-text-tertiary", children: label }),
        /* @__PURE__ */ jsxs("div", { className: "mt-2 flex items-baseline gap-1", children: [
          /* @__PURE__ */ jsx(
            "span",
            {
              className: cn(
                "text-2xl font-semibold tabular-nums tracking-tight",
                TONE_CLASS[tone]
              ),
              children: formatValue(value, format)
            }
          ),
          unit ? /* @__PURE__ */ jsx("span", { className: "text-xs text-text-tertiary", children: unit }) : null
        ] }),
        (delta || sparkline) && /* @__PURE__ */ jsxs("div", { className: "mt-2 flex items-center justify-between", children: [
          delta ? /* @__PURE__ */ jsxs(
            "span",
            {
              className: cn(
                "text-xs tabular-nums",
                TONE_CLASS[deltaTone],
                deltaTone === "neutral" && "text-text-tertiary"
              ),
              children: [
                formatDelta(delta),
                " ",
                delta.period ? /* @__PURE__ */ jsxs("span", { className: "text-text-tertiary", children: [
                  "\xB7 ",
                  delta.period
                ] }) : null
              ]
            }
          ) : /* @__PURE__ */ jsx("span", {}),
          sparkline
        ] })
      ]
    }
  );
}

// src/widgets/page-header.tsx
import { cn as cn2 } from "@modootoday/ui-primitives/core";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var BADGE_TONE = {
  neutral: "bg-surface-sunken text-text-secondary",
  brand: "bg-accent-soft text-text-brand",
  warning: "bg-signal-warning/10 text-signal-warning",
  danger: "bg-signal-danger/10 text-signal-danger"
};
function PageHeader({
  title,
  description,
  breadcrumbs,
  badge,
  meta,
  primaryAction,
  secondaryActions,
  tabs,
  className
}) {
  return /* @__PURE__ */ jsxs2(
    "header",
    {
      className: cn2(
        "flex flex-col gap-3 border-b border-stroke-subtle pb-3",
        className
      ),
      children: [
        breadcrumbs && breadcrumbs.length > 0 && /* @__PURE__ */ jsx2("nav", { className: "text-xs text-text-tertiary", "aria-label": "Breadcrumb", children: /* @__PURE__ */ jsx2("ol", { className: "flex flex-wrap items-center gap-1", children: breadcrumbs.map((b, i) => /* @__PURE__ */ jsxs2("li", { className: "flex items-center gap-1", children: [
          b.href ? /* @__PURE__ */ jsx2("a", { href: b.href, className: "hover:text-text-brand", children: b.label }) : /* @__PURE__ */ jsx2("span", { children: b.label }),
          i < breadcrumbs.length - 1 && /* @__PURE__ */ jsx2("span", { "aria-hidden": true, children: "/" })
        ] }, i)) }) }),
        /* @__PURE__ */ jsxs2("div", { className: "flex items-start justify-between gap-4", children: [
          /* @__PURE__ */ jsxs2("div", { className: "min-w-0 flex-1", children: [
            /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx2("h1", { className: "text-xl font-semibold tracking-tight text-text-primary", children: title }),
              badge && /* @__PURE__ */ jsx2(
                "span",
                {
                  className: cn2(
                    "rounded-xs px-2 py-0.5 text-xs font-medium",
                    BADGE_TONE[badge.tone ?? "neutral"]
                  ),
                  children: badge.label
                }
              )
            ] }),
            description && /* @__PURE__ */ jsx2("p", { className: "mt-1 text-sm text-text-secondary", children: description }),
            meta && /* @__PURE__ */ jsx2("div", { className: "mt-2 text-xs text-text-tertiary", children: meta })
          ] }),
          /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-2 shrink-0", children: [
            secondaryActions,
            primaryAction
          ] })
        ] }),
        tabs && /* @__PURE__ */ jsx2("div", { className: "-mb-3", children: tabs })
      ]
    }
  );
}

// src/widgets/empty-state.tsx
import { cn as cn3 } from "@modootoday/ui-primitives/core";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
function EmptyStateWidget({
  title,
  description,
  icon: Icon,
  action,
  className
}) {
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      className: cn3(
        "flex flex-col items-center justify-center gap-3 rounded-sm border border-dashed border-stroke-subtle bg-surface-raised p-8 text-center",
        className
      ),
      children: [
        Icon && /* @__PURE__ */ jsx3(Icon, { className: "h-6 w-6 text-text-tertiary" }),
        /* @__PURE__ */ jsxs3("div", { className: "space-y-1", children: [
          /* @__PURE__ */ jsx3("p", { className: "text-sm font-medium text-text-primary", children: title }),
          description && /* @__PURE__ */ jsx3("p", { className: "text-xs text-text-tertiary", children: description })
        ] }),
        action && /* @__PURE__ */ jsx3("div", { className: "mt-1", children: action })
      ]
    }
  );
}

// src/widgets/loading-state.tsx
import { cn as cn4 } from "@modootoday/ui-primitives/core";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
function LoadingStateWidget({
  variant = "skeleton",
  rows = 3,
  label = "Loading",
  className
}) {
  if (variant === "spinner") {
    return /* @__PURE__ */ jsxs4(
      "div",
      {
        className: cn4("flex items-center justify-center p-6", className),
        role: "status",
        "aria-label": label,
        children: [
          /* @__PURE__ */ jsx4("div", { className: "h-5 w-5 animate-spin rounded-xs border-2 border-stroke-subtle border-t-accent" }),
          /* @__PURE__ */ jsx4("span", { className: "sr-only", children: label })
        ]
      }
    );
  }
  if (variant === "shimmer-block") {
    return /* @__PURE__ */ jsx4(
      "div",
      {
        className: cn4("skeleton-shimmer h-24 w-full rounded-sm", className),
        role: "status",
        "aria-label": label
      }
    );
  }
  return /* @__PURE__ */ jsx4(
    "div",
    {
      className: cn4("flex flex-col gap-2", className),
      role: "status",
      "aria-label": label,
      children: Array.from({ length: rows }).map((_, i) => /* @__PURE__ */ jsx4("div", { className: "skeleton-shimmer h-4 w-full rounded-xs" }, i))
    }
  );
}

// src/widgets/sparkline.tsx
import { cn as cn5 } from "@modootoday/ui-primitives/core";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var TONE_STROKE = {
  neutral: "stroke-text-tertiary",
  brand: "stroke-accent",
  positive: "stroke-signal-success",
  negative: "stroke-signal-danger",
  warning: "stroke-signal-warning"
};
var TONE_FILL = {
  neutral: "fill-text-tertiary",
  brand: "fill-accent",
  positive: "fill-signal-success",
  negative: "fill-signal-danger",
  warning: "fill-signal-warning"
};
function SparklineWidget({
  data,
  tone = "brand",
  width = 80,
  height = 24,
  showEndDot = true,
  className
}) {
  if (data.length === 0) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;
  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = height - (v - min) / range * height;
    return { x, y };
  });
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const lastPoint = points[points.length - 1];
  return /* @__PURE__ */ jsxs5(
    "svg",
    {
      width,
      height,
      className: cn5("inline-block", className),
      viewBox: `0 0 ${width} ${height}`,
      "aria-hidden": true,
      children: [
        /* @__PURE__ */ jsx5(
          "path",
          {
            d: path,
            fill: "none",
            strokeWidth: 1.5,
            className: TONE_STROKE[tone]
          }
        ),
        showEndDot && lastPoint && /* @__PURE__ */ jsx5(
          "circle",
          {
            cx: lastPoint.x,
            cy: lastPoint.y,
            r: 2,
            className: TONE_FILL[tone]
          }
        )
      ]
    }
  );
}

// src/widgets/score-bar.tsx
import { cn as cn6 } from "@modootoday/ui-primitives/core";
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var TONE_FILL2 = {
  neutral: "bg-text-tertiary",
  brand: "bg-accent",
  positive: "bg-signal-success",
  warning: "bg-signal-warning",
  negative: "bg-signal-danger"
};
var DEFAULT_THRESHOLDS = [
  { at: 0, tone: "negative" },
  { at: 33, tone: "warning" },
  { at: 67, tone: "positive" }
];
function pickTone(value, thresholds) {
  const sorted = [...thresholds].sort((a, b) => a.at - b.at);
  let tone = sorted[0]?.tone ?? "neutral";
  for (const t of sorted) if (value >= t.at) tone = t.tone;
  return tone;
}
function ScoreBarWidget({
  value,
  max = 100,
  thresholds = DEFAULT_THRESHOLDS,
  label,
  showValue = true,
  className
}) {
  const pct = Math.max(0, Math.min(100, value / max * 100));
  const tone = pickTone(value, thresholds);
  return /* @__PURE__ */ jsxs6("div", { className: cn6("flex flex-col gap-1", className), children: [
    (label || showValue) && /* @__PURE__ */ jsxs6("div", { className: "flex items-center justify-between text-xs", children: [
      label && /* @__PURE__ */ jsx6("span", { className: "text-text-secondary", children: label }),
      showValue && /* @__PURE__ */ jsxs6("span", { className: "tabular-nums text-text-tertiary", children: [
        Math.round(value),
        /* @__PURE__ */ jsxs6("span", { className: "text-text-tertiary/60", children: [
          " / ",
          max
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx6("div", { className: "h-2 w-full rounded-xs bg-surface-sunken", children: /* @__PURE__ */ jsx6(
      "div",
      {
        className: cn6("h-full rounded-xs transition-all", TONE_FILL2[tone]),
        style: { width: `${pct}%` },
        role: "progressbar",
        "aria-valuenow": value,
        "aria-valuemin": 0,
        "aria-valuemax": max
      }
    ) })
  ] });
}

// src/widgets/chat-message.tsx
import { cn as cn7 } from "@modootoday/ui-primitives/core";
import { jsx as jsx7, jsxs as jsxs7 } from "react/jsx-runtime";
function ChatMessageWidget({
  role,
  author,
  timestamp,
  state = "complete",
  actions,
  children,
  className
}) {
  if (role === "system") {
    return /* @__PURE__ */ jsx7(
      "div",
      {
        className: cn7(
          "mx-auto max-w-2xl rounded-xs bg-surface-sunken px-3 py-2 text-center text-xs text-text-tertiary",
          className
        ),
        children
      }
    );
  }
  if (role === "user") {
    return /* @__PURE__ */ jsx7("div", { className: cn7("flex justify-end", className), children: /* @__PURE__ */ jsx7("div", { className: "max-w-3xl rounded-sm bg-surface-sunken px-4 py-3 text-md text-text-primary", children }) });
  }
  return /* @__PURE__ */ jsxs7(
    "article",
    {
      className: cn7(
        "group flex max-w-3xl flex-col gap-2 py-3",
        state === "error" && "opacity-90",
        className
      ),
      "aria-busy": state === "streaming" || void 0,
      children: [
        author && /* @__PURE__ */ jsxs7("header", { className: "flex items-center gap-2 text-xs text-text-tertiary", children: [
          author.avatar && /* @__PURE__ */ jsx7(
            "img",
            {
              src: author.avatar,
              alt: "",
              className: "h-5 w-5 rounded-xs object-cover",
              "aria-hidden": true
            }
          ),
          /* @__PURE__ */ jsx7("span", { className: "font-medium text-text-secondary", children: author.name }),
          timestamp && /* @__PURE__ */ jsx7("time", { className: "tabular-nums", children: new Date(timestamp).toLocaleTimeString("ko-KR", {
            hour: "2-digit",
            minute: "2-digit"
          }) })
        ] }),
        /* @__PURE__ */ jsx7(
          "div",
          {
            className: cn7(
              "prose-sm max-w-none text-md leading-relaxed text-text-primary",
              state === "streaming" && "after:ml-1 after:inline-block after:h-[1em] after:w-[2px] after:animate-pulse after:bg-accent after:align-middle"
            ),
            children
          }
        ),
        actions && /* @__PURE__ */ jsx7("footer", { className: "opacity-0 transition-opacity group-hover:opacity-100", children: actions })
      ]
    }
  );
}

// src/widgets/line-chart.tsx
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart as RcLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { cn as cn8 } from "@modootoday/ui-primitives/core";
import { jsx as jsx8, jsxs as jsxs8 } from "react/jsx-runtime";
var TONE_VAR = {
  brand: "hsl(var(--blue-500))",
  info: "hsl(var(--sky-500))",
  warning: "hsl(var(--amber-500))",
  positive: "hsl(var(--green-500))",
  negative: "hsl(var(--red-500))",
  neutral: "hsl(var(--slate-500))"
};
function LineChartWidget({
  series,
  height = 200,
  showLegend = false,
  showGrid = true,
  loading,
  error,
  className
}) {
  if (loading) {
    return /* @__PURE__ */ jsx8(
      "div",
      {
        className: cn8("skeleton-shimmer w-full rounded-sm", className),
        style: { height },
        "aria-label": "Loading chart"
      }
    );
  }
  if (error || series.length === 0) {
    return /* @__PURE__ */ jsx8(
      "div",
      {
        className: cn8(
          "flex w-full items-center justify-center rounded-sm border border-dashed border-stroke-subtle text-sm",
          error ? "text-signal-danger" : "text-text-tertiary",
          className
        ),
        style: { height },
        children: error ? "\uCC28\uD2B8\uB97C \uBD88\uB7EC\uC624\uC9C0 \uBABB\uD588\uC5B4\uC694" : "\uB370\uC774\uD130\uAC00 \uC5C6\uC5B4\uC694"
      }
    );
  }
  const merged = (series[0]?.points ?? []).map((p, i) => {
    const row = { x: p.x };
    for (const s of series) row[s.id] = s.points[i]?.y ?? 0;
    return row;
  });
  return /* @__PURE__ */ jsx8("div", { className: cn8("w-full", className), style: { height }, children: /* @__PURE__ */ jsx8(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs8(RcLineChart, { data: merged, children: [
    showGrid && /* @__PURE__ */ jsx8(
      CartesianGrid,
      {
        strokeDasharray: "3 3",
        stroke: "hsl(var(--stroke-subtle))"
      }
    ),
    /* @__PURE__ */ jsx8(XAxis, { dataKey: "x", fontSize: 10, stroke: "hsl(var(--text-tertiary))" }),
    /* @__PURE__ */ jsx8(YAxis, { fontSize: 10, stroke: "hsl(var(--text-tertiary))" }),
    /* @__PURE__ */ jsx8(
      Tooltip,
      {
        contentStyle: {
          fontSize: 11,
          borderRadius: 6,
          border: "1px solid hsl(var(--stroke-subtle))",
          background: "hsl(var(--surface-raised))",
          color: "hsl(var(--text-primary))"
        }
      }
    ),
    showLegend && /* @__PURE__ */ jsx8(Legend, {}),
    series.map((s) => /* @__PURE__ */ jsx8(
      Line,
      {
        type: "monotone",
        dataKey: s.id,
        name: s.label,
        stroke: TONE_VAR[s.tone ?? "brand"],
        strokeWidth: 1.5,
        dot: false
      },
      s.id
    ))
  ] }) }) });
}

// src/widgets/bar-chart.tsx
import {
  Bar,
  BarChart as RcBarChart,
  CartesianGrid as CartesianGrid2,
  Legend as Legend2,
  ResponsiveContainer as ResponsiveContainer2,
  Tooltip as Tooltip2,
  XAxis as XAxis2,
  YAxis as YAxis2
} from "recharts";
import { cn as cn9 } from "@modootoday/ui-primitives/core";
import { Fragment, jsx as jsx9, jsxs as jsxs9 } from "react/jsx-runtime";
var TONE_VAR2 = {
  brand: "hsl(var(--blue-500))",
  info: "hsl(var(--sky-500))",
  warning: "hsl(var(--amber-500))",
  positive: "hsl(var(--green-500))",
  negative: "hsl(var(--red-500))",
  neutral: "hsl(var(--slate-500))"
};
function BarChartWidget({
  series,
  height = 200,
  orientation = "vertical",
  stacked = false,
  showLegend = false,
  showGrid = true,
  loading,
  error,
  className
}) {
  if (loading) {
    return /* @__PURE__ */ jsx9(
      "div",
      {
        className: cn9("skeleton-shimmer w-full rounded-sm", className),
        style: { height }
      }
    );
  }
  if (error || series.length === 0) {
    return /* @__PURE__ */ jsx9(
      "div",
      {
        className: cn9(
          "flex w-full items-center justify-center rounded-sm border border-dashed border-stroke-subtle text-sm",
          error ? "text-signal-danger" : "text-text-tertiary",
          className
        ),
        style: { height },
        children: error ? "\uCC28\uD2B8\uB97C \uBD88\uB7EC\uC624\uC9C0 \uBABB\uD588\uC5B4\uC694" : "\uB370\uC774\uD130\uAC00 \uC5C6\uC5B4\uC694"
      }
    );
  }
  const merged = (series[0]?.points ?? []).map((p, i) => {
    const row = { x: p.x };
    for (const s of series) row[s.id] = s.points[i]?.y ?? 0;
    return row;
  });
  const horiz = orientation === "horizontal";
  return /* @__PURE__ */ jsx9("div", { className: cn9("w-full", className), style: { height }, children: /* @__PURE__ */ jsx9(ResponsiveContainer2, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs9(RcBarChart, { data: merged, layout: horiz ? "vertical" : "horizontal", children: [
    showGrid && /* @__PURE__ */ jsx9(
      CartesianGrid2,
      {
        strokeDasharray: "3 3",
        stroke: "hsl(var(--stroke-subtle))"
      }
    ),
    horiz ? /* @__PURE__ */ jsxs9(Fragment, { children: [
      /* @__PURE__ */ jsx9(
        XAxis2,
        {
          type: "number",
          fontSize: 10,
          stroke: "hsl(var(--text-tertiary))"
        }
      ),
      /* @__PURE__ */ jsx9(
        YAxis2,
        {
          dataKey: "x",
          type: "category",
          fontSize: 10,
          stroke: "hsl(var(--text-tertiary))"
        }
      )
    ] }) : /* @__PURE__ */ jsxs9(Fragment, { children: [
      /* @__PURE__ */ jsx9(
        XAxis2,
        {
          dataKey: "x",
          fontSize: 10,
          stroke: "hsl(var(--text-tertiary))"
        }
      ),
      /* @__PURE__ */ jsx9(YAxis2, { fontSize: 10, stroke: "hsl(var(--text-tertiary))" })
    ] }),
    /* @__PURE__ */ jsx9(
      Tooltip2,
      {
        contentStyle: {
          fontSize: 11,
          borderRadius: 6,
          border: "1px solid hsl(var(--stroke-subtle))",
          background: "hsl(var(--surface-raised))",
          color: "hsl(var(--text-primary))"
        }
      }
    ),
    showLegend && /* @__PURE__ */ jsx9(Legend2, {}),
    series.map((s) => /* @__PURE__ */ jsx9(
      Bar,
      {
        dataKey: s.id,
        name: s.label,
        fill: TONE_VAR2[s.tone ?? "brand"],
        stackId: stacked ? "stack" : void 0,
        radius: [2, 2, 0, 0]
      },
      s.id
    ))
  ] }) }) });
}

// src/widgets/index.ts
import {
  DataTable
} from "@modootoday/ui-table/data-table";

export {
  KpiCard,
  PageHeader,
  EmptyStateWidget,
  LoadingStateWidget,
  SparklineWidget,
  ScoreBarWidget,
  ChatMessageWidget,
  LineChartWidget,
  BarChartWidget,
  DataTable
};
//# sourceMappingURL=chunk-MYD3JATT.js.map